import FadeIn from '../components/FadeIn';
import { Link } from 'react-router-dom';
import { Wind, Flame, Leaf, Circle, Heart, Waves } from 'lucide-react';

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/60gea409_1000001871.jpg",
  gong: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/alpxg7xy_website%20photo%203.jpg",
  indoor: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/bdoytqmu_website%20photo%204.jpg",
  lotus: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/d1t7n4w6_website%20photo%20one.png",
};

const EXPERIENCES = [
  {
    icon: <Waves size={20} strokeWidth={1.5} />,
    title: 'Yoga & Somatic Movement',
    facilitator: 'Facilitated by Ashley',
    desc: 'Gentle, intentional movement to reconnect with your body and create safety within',
  },
  {
    icon: <Wind size={20} strokeWidth={1.5} />,
    title: '9D Breathwork Journey',
    facilitator: 'Facilitated by Harmony',
    desc: 'An immersive breathwork experience designed to unlock and release stored emotions',
  },
  {
    icon: <Flame size={20} strokeWidth={1.5} />,
    title: 'Fire Release Ceremony',
    facilitator: 'Facilitated by Jennie',
    desc: 'A powerful ritual to consciously let go of what you\'re ready to release',
  },
  {
    icon: <Leaf size={20} strokeWidth={1.5} />,
    title: 'Ancestral Clearing',
    facilitator: 'Facilitated by Elizabeth',
    desc: 'Bring awareness to inherited patterns and begin releasing what is not yours to carry',
  },
  {
    icon: <Circle size={20} strokeWidth={1.5} />,
    title: 'Unconscious Integration',
    facilitator: 'Facilitated by Jennie',
    desc: 'Understand your patterns, triggers, and internal responses on a deeper level',
  },
  {
    icon: <Heart size={20} strokeWidth={1.5} />,
    title: 'Nervous System Regulation',
    facilitator: 'Facilitated by Jennie',
    desc: 'Learn tools to feel safe, grounded, and supported in your daily life',
  },
];

const DAYS = [
  {
    day: 'Day 1',
    title: 'Regulate + Arrive',
    desc: 'Land, slow down, and reconnect with your body through grounding practices and nervous system support',
    bg: '#F7F4EF',
  },
  {
    day: 'Day 2',
    title: 'Reveal + Release',
    desc: 'Go deep with shadow work, a 9D breathwork journey, and a powerful fire release ceremony',
    bg: '#EAE3D9',
  },
  {
    day: 'Day 3',
    title: 'Integrate + Rise',
    desc: 'Anchor your transformation through integration, identity work, and embodied practices',
    bg: '#F7F4EF',
  },
];

const INVESTMENT = [
  {
    tier: 'Early Bird',
    price: '$777 – $888',
    note: 'Limited spots available',
    highlight: false,
  },
  {
    tier: 'Standard',
    price: '$1,111',
    note: 'Full investment',
    highlight: true,
  },
  {
    tier: 'VIP',
    price: '$1,444 – $1,555',
    note: 'Deeper support & integration',
    highlight: false,
  },
];

const RetreatPage = () => {
  return (
    <div data-testid="retreat-page" style={{ background: '#F7F4EF' }}>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section
        data-testid="retreat-hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Retreat" className="w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(160deg, rgba(247,244,239,0.90) 0%, rgba(247,244,239,0.80) 50%, rgba(234,227,217,0.88) 100%)' }}
          />
        </div>
        <div
          className="relative z-10 max-w-4xl mx-auto px-6 text-center hero-animate"
          style={{ paddingTop: '130px', paddingBottom: '90px' }}
        >
          <p
            className="text-xs tracking-[0.35em] uppercase font-medium mb-4"
            style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}
          >
            3-Day Immersion Retreat
          </p>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4"
            style={{ color: '#2B2B2B', letterSpacing: '-0.02em' }}
          >
            Regulate to Elevate
          </h1>
          <p
            className="font-serif text-xl sm:text-2xl mb-3"
            style={{ color: '#7E8C74', fontStyle: 'italic' }}
          >
            A Somatic & Shadow Work Immersion
          </p>
          <div
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-8"
            style={{ background: 'rgba(198,169,107,0.15)', border: '1px solid rgba(198,169,107,0.35)' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="#C6A96B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span className="text-sm font-medium" style={{ color: '#C6A96B', fontFamily: 'Inter, sans-serif', letterSpacing: '0.03em' }}>
              September 11, 2026, 3:00 PM &mdash; September 13, 2026, 3:30 PM
            </span>
          </div>
          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-12"
            style={{ color: 'rgba(43,43,43,0.7)', fontFamily: 'Inter, sans-serif' }}
          >
            A 3-day retreat for nervous system healing, emotional release, and deep self-reconnection
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://forms.gle/xgrDjooP9PSTyWE16"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="retreat-apply-btn"
              className="rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85"
              style={{ background: '#A8B5A2', color: '#2B2B2B', padding: '16px 38px', fontFamily: 'Inter, sans-serif' }}
            >
              Reserve Your Spot
            </a>
            <a
              href="#retreat-experience"
              data-testid="retreat-view-btn"
              className="rounded-full text-sm font-medium transition-all duration-300"
              style={{ border: '1.5px solid #C6A96B', color: '#C6A96B', padding: '15px 38px', fontFamily: 'Inter, sans-serif' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#C6A96B'; e.currentTarget.style.color = '#2B2B2B'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C6A96B'; }}
            >
              View Experience
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs tracking-widest uppercase" style={{ color: '#A8B5A2', fontFamily: 'Inter, sans-serif' }}>Scroll</span>
          <div style={{ width: '1px', height: '48px', background: 'linear-gradient(to bottom, #A8B5A2, transparent)' }} />
        </div>
      </section>

      {/* ── EMOTIONAL HOOK ───────────────────────────────────────────── */}
      <section data-testid="retreat-hook" className="py-24 md:py-32" style={{ background: '#EAE3D9' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <FadeIn>
            <div style={{ width: '1px', height: '48px', background: '#C6A96B', margin: '0 auto 32px' }} />
            <p
              className="font-serif text-2xl sm:text-3xl leading-relaxed mb-6"
              style={{ color: '#2B2B2B', fontStyle: 'italic' }}
            >
              You don't need to keep holding it all together.
            </p>
            <p
              className="font-serif text-xl sm:text-2xl leading-relaxed mb-10"
              style={{ color: '#7E8C74', fontStyle: 'italic' }}
            >
              You need a space where your body finally feels safe enough to let go.
            </p>
            <p
              className="text-base leading-relaxed"
              style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif', maxWidth: '640px', margin: '0 auto' }}
            >
              This retreat is designed to support you in slowing down, reconnecting, and releasing what your body has been carrying—so you can move forward with clarity, regulation, and self-trust.
            </p>
            <div style={{ width: '1px', height: '48px', background: '#C6A96B', margin: '32px auto 0' }} />
          </FadeIn>
        </div>
      </section>

      {/* ── WHAT YOU'LL EXPERIENCE ───────────────────────────────────── */}
      <section id="retreat-experience" data-testid="retreat-experiences" className="py-24 md:py-32" style={{ background: '#F7F4EF' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                The Immersion
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl" style={{ color: '#2B2B2B' }}>
                What You'll Experience
              </h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPERIENCES.map((exp, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div
                  className="p-8 rounded-3xl h-full"
                  style={{ background: '#EAE3D9', boxShadow: '0 8px 40px rgba(126,140,116,0.07)' }}
                >
                  <div
                    className="flex items-center justify-center mb-5 rounded-full"
                    style={{ width: '44px', height: '44px', background: 'rgba(198,169,107,0.15)', color: '#C6A96B' }}
                  >
                    {exp.icon}
                  </div>
                  <h3 className="font-serif text-lg mb-1" style={{ color: '#2B2B2B' }}>{exp.title}</h3>
                  {exp.facilitator && (
                    <p className="text-xs mb-3" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
                      {exp.facilitator}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.72)', fontFamily: 'Inter, sans-serif' }}>
                    {exp.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRANSFORMATION ───────────────────────────────────────────── */}
      <section data-testid="retreat-transformation" className="py-24 md:py-32" style={{ background: '#7E8C74' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-4" style={{ color: '#F7F4EF' }}>
                This Isn't About Learning More.
              </h2>
              <p className="font-serif text-2xl sm:text-3xl" style={{ color: 'rgba(247,244,239,0.75)', fontStyle: 'italic' }}>
                It's About Finally Feeling Different.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={150}>
            <p
              className="text-center text-sm tracking-[0.2em] uppercase font-medium mb-8"
              style={{ color: 'rgba(247,244,239,0.65)', fontFamily: 'Inter, sans-serif' }}
            >
              By the end of this experience, you will:
            </p>
          </FadeIn>
          <div className="space-y-4 max-w-2xl mx-auto">
            {[
              'Feel more grounded and regulated',
              'Release emotional weight your body has been holding',
              'Gain clarity around your patterns and behaviors',
              'Feel more connected to yourself',
              'Leave with tools that actually support you in real life',
            ].map((item, i) => (
              <FadeIn key={i} delay={200 + i * 90}>
                <div
                  className="flex items-center gap-5 py-4 px-6 rounded-2xl"
                  style={{ background: 'rgba(247,244,239,0.1)' }}
                >
                  <div className="flex-shrink-0" style={{ width: '2px', height: '18px', background: '#C6A96B', borderRadius: '2px' }} />
                  <p className="text-base" style={{ color: '#F7F4EF', fontFamily: 'Inter, sans-serif' }}>{item}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE EXPERIENCE (3 DAYS) ──────────────────────────────────── */}
      <section data-testid="retreat-days" className="py-24 md:py-32" style={{ background: '#EAE3D9' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Your Journey
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl" style={{ color: '#2B2B2B' }}>
                The Experience
              </h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {DAYS.map((d, i) => (
              <FadeIn key={i} delay={i * 130}>
                <div
                  className="p-10 rounded-3xl h-full"
                  style={{ background: '#F7F4EF', boxShadow: '0 8px 40px rgba(126,140,116,0.08)' }}
                >
                  <p
                    className="font-serif text-4xl font-light mb-4"
                    style={{ color: '#C6A96B' }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <p
                    className="text-xs tracking-[0.2em] uppercase font-medium mb-2"
                    style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}
                  >
                    {d.day}
                  </p>
                  <h3 className="font-serif text-xl mb-4" style={{ color: '#2B2B2B' }}>{d.title}</h3>
                  <div style={{ width: '24px', height: '1px', background: '#C6A96B', marginBottom: '16px' }} />
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.72)', fontFamily: 'Inter, sans-serif' }}>
                    {d.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── VIP ──────────────────────────────────────────────────────── */}
      <section data-testid="retreat-vip" className="py-24 md:py-32" style={{ background: '#F7F4EF' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#C6A96B', fontFamily: 'Inter, sans-serif' }}>
                Elevated Support
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl mb-6" style={{ color: '#2B2B2B' }}>
                VIP: Deeper Support & Integration
              </h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: 'rgba(43,43,43,0.7)', fontFamily: 'Inter, sans-serif' }}>
                For those ready for a more personalized and supported experience — including a curated gift bag sponsored by Devi Beauty Co.
              </p>
              <div className="space-y-4">
                {[
                  'Private 1:1 integration session',
                  'Priority guidance during the retreat',
                  'Post-retreat integration call',
                  'Curated grounding gift bag by Devi Beauty Co',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-2" style={{ width: '16px', height: '1px', background: '#C6A96B' }} />
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.78)', fontFamily: 'Inter, sans-serif' }}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="overflow-hidden rounded-3xl" style={{ boxShadow: '0 24px 64px rgba(126,140,116,0.14)' }}>
                <img
                  src={IMAGES.gong}
                  alt="VIP retreat experience"
                  className="w-full object-cover img-hover"
                  style={{ height: '420px' }}
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── INVESTMENT ───────────────────────────────────────────────── */}
      <section data-testid="retreat-investment" className="py-24 md:py-32" style={{ background: '#EAE3D9' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Your Investment
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl" style={{ color: '#2B2B2B' }}>
                Investment
              </h2>
              <p className="text-sm mt-4" style={{ color: 'rgba(43,43,43,0.55)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
                Payment plans available
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6 mb-14">
            {INVESTMENT.map((tier, i) => (
              <FadeIn key={i} delay={i * 120}>
                <div
                  className="p-10 rounded-3xl text-center h-full flex flex-col justify-between"
                  style={{
                    background: tier.highlight ? '#7E8C74' : '#F7F4EF',
                    boxShadow: tier.highlight ? '0 20px 60px rgba(126,140,116,0.2)' : '0 8px 40px rgba(126,140,116,0.07)',
                    transform: tier.highlight ? 'scale(1.03)' : 'scale(1)',
                  }}
                >
                  <div>
                    <p
                      className="text-xs tracking-[0.2em] uppercase font-medium mb-4"
                      style={{ color: tier.highlight ? 'rgba(247,244,239,0.7)' : '#7E8C74', fontFamily: 'Inter, sans-serif' }}
                    >
                      {tier.tier}
                    </p>
                    <p
                      className="font-serif text-3xl sm:text-4xl mb-4"
                      style={{ color: tier.highlight ? '#F7F4EF' : '#C6A96B' }}
                    >
                      {tier.price}
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: tier.highlight ? 'rgba(247,244,239,0.65)' : 'rgba(43,43,43,0.55)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}
                    >
                      {tier.note}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <div className="text-center">
            <a
              href="https://forms.gle/xgrDjooP9PSTyWE16"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="retreat-reserve-btn"
              className="inline-block rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85"
              style={{ background: '#C6A96B', color: '#2B2B2B', padding: '16px 48px', fontFamily: 'Inter, sans-serif' }}
            >
              Reserve Your Spot
            </a>
          </div>
        </div>
      </section>

      {/* ── AMENITIES ────────────────────────────────────────────────── */}
      <section data-testid="retreat-amenities" className="py-24 md:py-32" style={{ background: '#F7F4EF' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Everything Included
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl" style={{ color: '#2B2B2B' }}>
                Your Stay, Taken Care Of
              </h2>
              <p className="text-base mt-4 max-w-xl mx-auto" style={{ color: 'rgba(43,43,43,0.6)', fontFamily: 'Inter, sans-serif' }}>
                So you can arrive with nothing on your mind except showing up fully.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px' }}>
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                ),
                title: 'Private Accommodations',
                desc: 'Comfortable Airbnb lodging included for the full duration of the retreat',
              },
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px' }}>
                    <path d="M18 8h1a4 4 0 010 8h-1" /><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" /><line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" />
                  </svg>
                ),
                title: 'All Organic Meals & Snacks',
                desc: 'Nourishing, thoughtfully prepared organic food throughout your entire stay',
              },
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px' }}>
                    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                ),
                title: 'Natural Juice Bar',
                desc: 'Fresh cold-pressed juices and herbal elixirs available throughout the day',
              },
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px' }}>
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                ),
                title: 'Indoor Restrooms',
                desc: 'Full indoor facilities on-site for your comfort and convenience',
              },
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '22px', height: '22px' }}>
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
                  </svg>
                ),
                title: 'Emotional Support',
                desc: 'Trauma-aware facilitators available to hold space and support you throughout',
              },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 90}>
                <div
                  className="flex items-start gap-5 p-7 rounded-2xl h-full"
                  style={{ background: '#EAE3D9', boxShadow: '0 6px 28px rgba(126,140,116,0.07)' }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0 rounded-full"
                    style={{ width: '46px', height: '46px', background: 'rgba(198,169,107,0.15)', color: '#C6A96B' }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-serif text-base mb-2" style={{ color: '#2B2B2B' }}>{item.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.65)', fontFamily: 'Inter, sans-serif' }}>{item.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── MEET YOUR FACILITATOR ────────────────────────────────────── */}
      <section data-testid="retreat-facilitator" className="py-24 md:py-32" style={{ background: '#F7F4EF' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div className="overflow-hidden rounded-3xl" style={{ boxShadow: '0 24px 64px rgba(126,140,116,0.14)' }}>
                <img
                  src={IMAGES.indoor}
                  alt="Jennie — Retreat Facilitator"
                  className="w-full object-cover img-hover"
                  style={{ height: '520px' }}
                />
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                Your Lead Facilitator
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl mb-8" style={{ color: '#2B2B2B' }}>
                Meet Jennie
              </h2>
              <div className="space-y-5 mb-8">
                <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                  Jennie is a somatic healing facilitator and founder of Resonant Healing, devoted to guiding others back into safety within their own bodies.
                </p>
                <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                  Her work focuses on nervous system regulation, shadow work, and emotional integration to support deep, lasting transformation. She creates trauma-aware, consent-based spaces where individuals can gently meet themselves, process what they've been holding, and reconnect with who they truly are.
                </p>
              </div>
              <div
                className="p-6 rounded-2xl"
                style={{ background: '#EAE3D9', borderLeft: '3px solid #C6A96B' }}
              >
                <p className="text-xs tracking-[0.2em] uppercase font-medium mb-3" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                  Guest Facilitators
                </p>
                <div className="space-y-2">
                  {[
                    { name: 'Harmony', role: '9D Breathwork' },
                    { name: 'Elizabeth', role: 'Ancestral Clearing' },
                    { name: 'Ashley', role: 'Yoga' },
                    { name: 'Devi Beauty Co', role: 'VIP Gift Bag Sponsor' },
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div style={{ width: '14px', height: '1px', background: '#C6A96B', flexShrink: 0 }} />
                      <p className="text-sm" style={{ color: 'rgba(43,43,43,0.75)', fontFamily: 'Inter, sans-serif' }}>
                        <span style={{ fontWeight: '500' }}>{f.name}</span>
                        <span style={{ color: 'rgba(43,43,43,0.5)' }}> — {f.role}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── GUEST FACILITATORS ───────────────────────────────────────── */}
      <section data-testid="retreat-guest-facilitators" className="py-24 md:py-32" style={{ background: '#EAE3D9' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                The Team
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl" style={{ color: '#2B2B2B' }}>
                Guest Facilitators
              </h2>
            </div>
          </FadeIn>

          {/* Elizabeth */}
          <FadeIn delay={100}>
            <div
              className="rounded-3xl overflow-hidden"
              style={{ background: '#F7F4EF', boxShadow: '0 16px 56px rgba(126,140,116,0.10)' }}
            >
              <div className="grid md:grid-cols-5 gap-0">
                {/* Photo */}
                <div
                  className="md:col-span-2 flex items-center justify-center overflow-hidden"
                  style={{ background: '#F0EDE8', minHeight: '360px' }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_resonant-regulation/artifacts/a2tdjsqy_ElizabethPromo.png"
                    alt="Elizabeth Kipp — Ancestral Clearing Practitioner"
                    className="w-full h-full object-cover"
                    style={{ mixBlendMode: 'multiply', minHeight: '360px' }}
                  />
                </div>

                {/* Content */}
                <div className="md:col-span-3 p-10 md:p-14">
                  <div className="flex flex-wrap items-center gap-4 mb-2">
                    <h3 className="font-serif text-2xl sm:text-3xl" style={{ color: '#2B2B2B' }}>
                      Elizabeth Kipp
                    </h3>
                    <span
                      className="text-xs tracking-[0.2em] uppercase font-medium px-4 py-1 rounded-full"
                      style={{ background: 'rgba(198,169,107,0.15)', color: '#C6A96B', fontFamily: 'Inter, sans-serif' }}
                    >
                      Ancestral Clearing Practitioner
                    </span>
                  </div>

                  {/* Credentials row */}
                  <div className="flex flex-wrap gap-2 mb-8 mt-3">
                    {[
                      'Stress Management & Historical Trauma Specialist',
                      'Certified Addiction Recovery Coach',
                      'International Best-Selling Author',
                    ].map((cred, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 rounded-full"
                        style={{ background: '#EAE3D9', color: 'rgba(43,43,43,0.65)', fontFamily: 'Inter, sans-serif' }}
                      >
                        {cred}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-5 mb-8">
                    <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                      Elizabeth blends neuroscience, somatic awareness, and spiritual wisdom to help people release ancestral and personal patterns of suffering. By integrating these modalities with meditation, breathwork, and energy healing, she guides others towards balance and inner harmony.
                    </p>
                    <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                      Having healed from over 40 years of chronic pain, anxiety, and addiction, Elizabeth's mission is to help others reclaim their inner freedom, embody resilience, and live from their highest truth.
                    </p>
                  </div>

                  {/* Book callout */}
                  <div
                    className="flex items-start gap-5 p-5 rounded-2xl"
                    style={{ background: '#EAE3D9', borderLeft: '3px solid #C6A96B' }}
                  >
                    <div className="flex-shrink-0 mt-1" style={{ width: '20px', height: '20px', color: '#C6A96B' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.15em] uppercase font-medium mb-1" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                        Author
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.78)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
                        "The Way Through Chronic Pain: Tools To Reclaim Your Healing Power"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Harmony */}
          <FadeIn delay={150}>
            <div
              className="rounded-3xl overflow-hidden mt-8"
              style={{ background: '#F7F4EF', boxShadow: '0 16px 56px rgba(126,140,116,0.10)' }}
            >
              <div className="grid md:grid-cols-5 gap-0">
                {/* Photo */}
                <div className="md:col-span-2 overflow-hidden" style={{ minHeight: '360px' }}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_resonant-regulation/artifacts/plrx6xvo_HarmonyPromo.jpg"
                    alt="Harmony — 9D Breathwork Facilitator"
                    className="w-full h-full object-cover"
                    style={{ minHeight: '360px' }}
                  />
                </div>

                {/* Content */}
                <div className="md:col-span-3 p-10 md:p-14">
                  <div className="flex flex-wrap items-center gap-4 mb-2">
                    <h3 className="font-serif text-2xl sm:text-3xl" style={{ color: '#2B2B2B' }}>
                      Harmony
                    </h3>
                    <span
                      className="text-xs tracking-[0.2em] uppercase font-medium px-4 py-1 rounded-full"
                      style={{ background: 'rgba(198,169,107,0.15)', color: '#C6A96B', fontFamily: 'Inter, sans-serif' }}
                    >
                      9D Breathwork Facilitator
                    </span>
                  </div>

                  {/* Credentials */}
                  <div className="flex flex-wrap gap-2 mb-8 mt-3">
                    {[
                      'Founder, Rest & Recovery Camp',
                      'Certified 9D Breathwork Facilitator',
                      'Somatic Coach',
                      'Recovery Coach',
                    ].map((cred, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 rounded-full"
                        style={{ background: '#EAE3D9', color: 'rgba(43,43,43,0.65)', fontFamily: 'Inter, sans-serif' }}
                      >
                        {cred}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-4 mb-8">
                    <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                      Harmony brings transformative healing experiences to treatment centers, festivals, and retreat spaces. Her approach blends ancient wisdom with modern neuroscience, somatic practices, and sound healing — all grounded in a trauma-informed, no-B.S. philosophy: utilize any tool that has ever helped anyone for anything ever.
                    </p>
                    <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                      Through 9D Breathwork — a multi-dimensional journey combining breathwork, somatic therapy, guided meditation, and sound healing — Harmony guides participants into profound states of release, integration, and transformation.
                    </p>
                    <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
                      Come ready to breathe, feel, and remember who you've always been underneath it all.
                    </p>
                  </div>

                  {/* Website callout */}
                  <div
                    className="flex items-center gap-5 p-5 rounded-2xl"
                    style={{ background: '#EAE3D9', borderLeft: '3px solid #C6A96B' }}
                  >
                    <div className="flex-shrink-0" style={{ width: '20px', height: '20px', color: '#C6A96B' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.15em] uppercase font-medium mb-1" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
                        Learn More
                      </p>
                      <a
                        href="https://www.restandrecovery.love/9d-breathwork"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm transition-colors duration-200 hover:text-[#C6A96B]"
                        style={{ color: 'rgba(43,43,43,0.78)', fontFamily: 'Inter, sans-serif' }}
                      >
                        www.restandrecovery.love/9d-breathwork
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── SAFETY & TRUST ───────────────────────────────────────────── */}
      <section data-testid="retreat-safety" className="py-24 md:py-32" style={{ background: '#EAE3D9' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <FadeIn>
            <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74', fontFamily: 'Inter, sans-serif' }}>
              Our Commitment
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl mb-12" style={{ color: '#2B2B2B' }}>
              A Space Designed for Safety
            </h2>
          </FadeIn>
          <div className="space-y-5">
            {[
              'This experience is trauma-aware and guided with a consent-based approach.',
              'You are always invited to move at your own pace.',
              'Nothing is forced.',
              'You are supported exactly where you are.',
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 100}>
                <div
                  className="flex items-center gap-5 py-4 px-6 rounded-2xl text-left"
                  style={{ background: '#F7F4EF', boxShadow: '0 4px 20px rgba(126,140,116,0.06)' }}
                >
                  <div className="flex-shrink-0" style={{ width: '2px', height: '18px', background: '#C6A96B', borderRadius: '2px' }} />
                  <p className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.8)', fontFamily: 'Inter, sans-serif' }}>
                    {item}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────── */}
      <section data-testid="retreat-final-cta" className="py-24 md:py-32 text-center" style={{ background: '#7E8C74' }}>
        <div className="max-w-2xl mx-auto px-6">
          <FadeIn>
            <div style={{ width: '1px', height: '48px', background: 'rgba(247,244,239,0.3)', margin: '0 auto 28px' }} />
            <p
              className="text-xs tracking-[0.3em] uppercase font-medium mb-6"
              style={{ color: 'rgba(247,244,239,0.65)', fontFamily: 'Inter, sans-serif' }}
            >
              The Next Step
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl mb-5" style={{ color: '#F7F4EF' }}>
              If You Feel the Pull, Trust It.
            </h2>
            <p
              className="text-base leading-relaxed mb-10"
              style={{ color: 'rgba(247,244,239,0.75)', fontFamily: 'Inter, sans-serif' }}
            >
              This container is intentionally intimate to ensure depth, safety, and connection.
            </p>
            <a
              href="https://forms.gle/xgrDjooP9PSTyWE16"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="retreat-final-apply-btn"
              className="inline-block rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85 mb-4"
              style={{ background: '#C6A96B', color: '#2B2B2B', padding: '18px 52px', fontFamily: 'Inter, sans-serif' }}
            >
              Reserve Your Spot
            </a>
            <p className="text-xs" style={{ color: 'rgba(247,244,239,0.5)', fontFamily: 'Inter, sans-serif', fontStyle: 'italic' }}>
              Limited spots available
            </p>
          </FadeIn>
        </div>
      </section>

    </div>
  );
};

export default RetreatPage;
